/**
 * Helper to compute user display status and badge color based on API data.
 *
 * Rules:
 * DRIVER:
 * - user.status === false => Inactive (gray)
 * - approval_status === "pending" => Pending (yellow)
 * - approval_status === "approved" => Active (green)
 * - approval_status === "rejected" => Rejected (red)
 * - approval_status === "suspended" => Suspended (red)
 *
 * VENDOR:
 * - user.status === false => Inactive (gray)
 * - any store.status === false => Store Inactive (orange/yellow)
 * - otherwise => Active (green)
 *
 * OTHER ROLES:
 * - user.status === true => Active (green)
 * - user.status === false => Inactive (gray)
 *
 * @param {Object} user
 * @returns {{ label: string, color: 'success' | 'warning' | 'error' | 'default' }}
 */
export function getUserDisplayStatus(user) {
  if (!user) {
    return { label: 'Inactive', color: 'default' };
  }

  // Extract role and user type (case insensitive & null safe)
  const roleName = String(
    user?.Role?.role_name ||
    user?.Role?.name ||
    user?.role_name ||
    user?.role ||
    ''
  ).toUpperCase();

  const userType = String(
    user?.UserType?.type_name ||
    user?.UserType?.name ||
    user?.type_name ||
    user?.user_type ||
    ''
  ).toUpperCase();

  const isDriver = roleName === 'DRIVER' || userType === 'DRIVER';
  const isVendor =
    roleName === 'VENDOR' ||
    roleName === 'STORE' ||
    roleName === 'MERCHANT' ||
    userType === 'VENDOR' ||
    userType === 'STORE';

  // 1. DRIVER Rules
  if (isDriver) {
    if (user.status === false || user.status === 0 || user.status === 'false') {
      return { label: 'Inactive', color: 'default' };
    }

    const driverObj = user.driverProfile || user.driver_profile || user.driver || user.Driver || {};
    const rawApproval =
      driverObj.approval_status ||
      driverObj.approvalStatus ||
      user.approval_status ||
      user.approvalStatus ||
      '';

    const approvalStatus = String(rawApproval).toLowerCase().trim();

    if (approvalStatus === 'pending') {
      return { label: 'Pending', color: 'warning' };
    }
    if (approvalStatus === 'approved') {
      return { label: 'Active', color: 'success' };
    }
    if (approvalStatus === 'rejected') {
      return { label: 'Rejected', color: 'error' };
    }
    if (approvalStatus === 'suspended') {
      return { label: 'Suspended', color: 'error' };
    }

    if (user.status === true || user.status === 1 || user.status === 'true') {
      return { label: 'Active', color: 'success' };
    }
    return { label: 'Inactive', color: 'default' };
  }

  // 2. VENDOR Rules
  if (isVendor) {
    if (user.status === false || user.status === 0 || user.status === 'false') {
      return { label: 'Inactive', color: 'default' };
    }

    const rawStores = user.stores || user.Stores || user.store || user.Store;
    const stores = Array.isArray(rawStores)
      ? rawStores
      : rawStores
      ? [rawStores]
      : [];

    const hasInactiveStore = stores.some((s) => {
      if (!s) return false;
      const st = s.status !== undefined ? s.status : s.store_status;
      return st === false || st === 0 || st === 'false' || st === 'inactive';
    });

    if (hasInactiveStore) {
      return { label: 'Store Inactive', color: 'warning' };
    }

    return { label: 'Active', color: 'success' };
  }

  // 3. OTHER ROLES Rules
  if (user.status === false || user.status === 0 || user.status === 'false') {
    return { label: 'Inactive', color: 'default' };
  }

  return { label: 'Active', color: 'success' };
}
